// client/src/components/AdminGestionRegie.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminNavbar from './AdminNavbar';
import { FaSearch } from 'react-icons/fa';
import '../CSS/Dashboard.css';
import { auth, firestore } from '../../firebase';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
  documentId,
} from 'firebase/firestore';

/**
 * ----------------------------- Utils display -----------------------------
 */
const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    if (typeof value.toDate === 'function') {
      return value.toDate().toLocaleString('fr-FR');
    }
  } catch {}
  if (value instanceof Date) return value.toLocaleString('fr-FR');
  if (typeof value === 'string') return value;
  if (value?.seconds) {
    return new Date(value.seconds * 1000).toLocaleString('fr-FR');
  }
  return '—';
};

const getDisplayName = (user) => {
  const candidates = [
    user?.pseudo,
    user?.displayName,
    user?.name,
    user?.entreprise_name,
    user?.companyName,
  ]
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter(Boolean);
  return candidates[0] || '—';
};

const toLc = (s) => (s == null ? '' : String(s).toLowerCase());

/**
 * ------------------------- Résolution pseudo/UID -------------------------
 * Permet à l'humain de saisir pseudo ou UID; la DB reste 100% en UID.
 */
const resolveUserByHandleOrUid = async (fs, inputRaw) => {
  const input = String(inputRaw || '').trim();
  if (!input) return null;

  // 1) Essai direct par UID (doc id)
  try {
    const ref = doc(fs, 'users', input);
    const snap = await getDoc(ref);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  } catch {}

  // 2) Exact match sur champs usuels (ordre d'importance)
  const tries = [
    ['pseudo_lc', input.toLowerCase()],
    ['pseudo', input],
    ['displayName', input],
    ['entreprise_name', input],
    ['companyName', input],
    ['email', input.toLowerCase()],
  ];
  for (const [field, value] of tries) {
    try {
      const qRef = query(collection(fs, 'users'), where(field, '==', value));
      const snap = await getDocs(qRef);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    } catch {}
  }
  return null;
};

/**
 * ------------------------- Batches helpers Firestore -------------------------
 */
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Récupère les users par leurs UIDs en chunks de 10 (limite Firestore "in")
const batchFetchUsersByIds = async (fs, uids = []) => {
  const unique = [...new Set(uids.filter(Boolean))];
  if (unique.length === 0) return [];
  const chs = chunk(unique, 10);
  const rows = [];
  for (const c of chs) {
    try {
      const qRef = query(collection(fs, 'users'), where(documentId(), 'in', c));
      const snap = await getDocs(qRef);
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
    } catch {
      // ignore chunk errors; continue
    }
  }
  return rows;
};

// Source "complète" d'affiliés : ids de la collection "code promo"
// Avec cache sessionStorage pour éviter les relectures Firestore
const REGIE_AFF_UIDS_CACHE_KEY = 'admin_regie_all_aff_uids_v1';
const REGIE_AFF_UIDS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const fetchAllAffiliateUidsFromCodePromo = async (fs) => {
  try {
    const raw = sessionStorage.getItem(REGIE_AFF_UIDS_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Date.now() - (parsed.ts || 0) <= REGIE_AFF_UIDS_CACHE_TTL_MS) {
        return parsed.uids;
      }
    }
  } catch { /* noop */ }

  const promoCol = collection(fs, 'code promo');
  const snap = await getDocs(promoCol);
  const uids = snap.docs.map((d) => d.id);

  try {
    sessionStorage.setItem(REGIE_AFF_UIDS_CACHE_KEY, JSON.stringify({ ts: Date.now(), uids }));
  } catch { /* noop */ }

  return uids;
};

/**
 * ------------------------------ Composant UI ------------------------------
 * Objectif unique : attribuer des affiliés à une régie.
 */
const PAGE_SIZE = 20;

const AdminGestionRegie = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --------- Auto-identification : on récupère l'UID depuis AdminUsersViewer ---------
  const initialUidFromNav = useMemo(() => {
    const raw = location.state?.uid;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [location.state]);

  // ------- Régie courante (auto) -------
  const [currentRegieUid, setCurrentRegieUid] = useState(initialUidFromNav);
  const [regieDoc, setRegieDoc] = useState(null);
  const [regieLoading, setRegieLoading] = useState(false);
  const [regieError, setRegieError] = useState('');
  const [regieWarning, setRegieWarning] = useState('');

  // ------- (fallback rare) Champ si aucun UID n'a été passé via navigation -------
  const [regieInput, setRegieInput] = useState('');

  // ------- Affiliés déjà rattachés à la régie -------
  const [assignedAffiliates, setAssignedAffiliates] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);

  // ------- Suggestions : liste complète + recherche + pagination -------
  const [suggestUsers, setSuggestUsers] = useState([]); // users résolus
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  // ------- Recherche ciblée (pseudo/UID/email) -------
  const [lookupInput, setLookupInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupAffiliate, setLookupAffiliate] = useState(null);

  // ------- Actions courantes -------
  const [assigningUid, setAssigningUid] = useState(null);
  const [unassigningUid, setUnassigningUid] = useState(null);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(''), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  // Si on arrive **sans** UID (cas exceptionnel), on laisse l'admin saisir pseudo/UID
  const handleManualRegieLoad = useCallback(
    async (e) => {
      e.preventDefault();
      setRegieError('');
      if (!regieInput.trim()) {
        setRegieError('Indique un pseudo ou un UID de régie.');
        return;
      }
      try {
        setRegieLoading(true);
        const user = await resolveUserByHandleOrUid(firestore, regieInput.trim());
        if (!user) {
          setRegieError("Aucun utilisateur trouvé pour ce pseudo/UID.");
          return;
        }
        setCurrentRegieUid(user.id);
      } catch {
        setRegieError("Erreur lors de la résolution du pseudo/UID.");
      } finally {
        setRegieLoading(false);
      }
    },
    [regieInput]
  );

  // Charger la régie dès que currentRegieUid est défini (auto depuis navigation)
  useEffect(() => {
    if (!currentRegieUid) {
      setRegieDoc(null);
      setRegieError('');
      setRegieWarning('');
      return;
    }
    let active = true;
    setRegieLoading(true);
    setRegieError('');
    setRegieWarning('');
    (async () => {
      try {
        const ref = doc(firestore, 'users', currentRegieUid);
        const snap = await getDoc(ref);
        if (!active) return;
        if (!snap.exists()) {
          setRegieDoc(null);
          setRegieError("Aucun utilisateur trouvé pour cet identifiant.");
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setRegieDoc(data);
        if (data.role !== 'AffiliateRegie') {
          setRegieWarning(
            `Le compte est "${data.role || 'inconnu'}". Active la bascule Régie sur ce profil pour sécuriser la gestion.`
          );
        }
      } catch {
        if (!active) return;
        setRegieError("Erreur lors du chargement de la régie.");
      } finally {
        if (active) setRegieLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [currentRegieUid]);

  // Écoute dynamique des affiliés rattachés
  useEffect(() => {
    if (!currentRegieUid) {
      setAssignedAffiliates([]);
      setAssignedLoading(false);
      return;
    }
    setAssignedLoading(true);
    const qRef = query(collection(firestore, 'users'), where('regieUid', '==', currentRegieUid));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const aLabel = `${getDisplayName(a)} ${a.email || ''}`.toLowerCase();
          const bLabel = `${getDisplayName(b)} ${b.email || ''}`.toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
        setAssignedAffiliates(rows);
        setAssignedLoading(false);
      },
      () => {
        setAssignedLoading(false);
        setRegieError("Impossible de récupérer la liste des affiliés rattachés.");
      }
    );
    return () => unsub();
  }, [currentRegieUid]);

  // Suggestions : “code promo” ➜ users
  const reloadSuggestions = useCallback(async () => {
    if (!currentRegieUid) return;
    setSuggestLoading(true);
    setSuggestError('');
    try {
      const uids = await fetchAllAffiliateUidsFromCodePromo(firestore);
      const users = await batchFetchUsersByIds(firestore, uids);
      const filtered = users.filter((u) => ['affiliates', 'affiliatepending'].includes(u.role));
      filtered.sort((a, b) => {
        const aLabel = `${getDisplayName(a)} ${a.email || ''}`.toLowerCase();
        const bLabel = `${getDisplayName(b)} ${b.email || ''}`.toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
      setSuggestUsers(filtered);
    } catch {
      setSuggestError("Erreur lors du chargement des suggestions.");
    } finally {
      setSuggestLoading(false);
    }
  }, [currentRegieUid]);

  useEffect(() => {
    reloadSuggestions();
  }, [reloadSuggestions]);

  // Recherche ciblée (pseudo/UID/email)
  const handleLookupAffiliate = useCallback(
    async (e) => {
      e.preventDefault();
      setLookupError('');
      setLookupAffiliate(null);
      if (!currentRegieUid) {
        setLookupError('Régie non chargée.');
        return;
      }
      const handle = lookupInput.trim();
      if (!handle) {
        setLookupError('Saisis un pseudo, un UID ou un email affilié.');
        return;
      }
      setLookupLoading(true);
      try {
        const user = await resolveUserByHandleOrUid(firestore, handle);
        if (!user) {
          setLookupError("Aucun utilisateur trouvé pour ce pseudo/UID.");
          return;
        }
        if (!['affiliates', 'affiliatepending'].includes(user.role)) {
          setLookupError(
            `Rôle actuel: "${user.role || 'inconnu'}". Seuls les affiliés indépendants peuvent être rattachés.`
          );
        }
        setLookupAffiliate(user);
      } catch {
        setLookupError("Erreur lors de la recherche de l'utilisateur.");
      } finally {
        setLookupLoading(false);
      }
    },
    [currentRegieUid, lookupInput]
  );

  // Attribution (bouton)
  const assignAffiliate = useCallback(
    async (affiliateUser) => {
      if (!currentRegieUid || !affiliateUser?.id) return;
      if (!['affiliates', 'affiliatepending'].includes(affiliateUser.role)) {
        setFlash("Ce compte n'est pas un affilié actif/en attente.");
        return;
      }
      const alreadyOnOther =
        affiliateUser.regieUid && affiliateUser.regieUid !== currentRegieUid;
      if (alreadyOnOther) {
        const ok = window.confirm(
          `Cet affilié est déjà rattaché à ${affiliateUser.regieUid}. Continuer et le déplacer vers cette régie ?`
        );
        if (!ok) return;
      }
      try {
        setAssigningUid(affiliateUser.id);
        const batch = writeBatch(firestore);
        const affiliateRef = doc(firestore, 'users', affiliateUser.id);
        batch.update(affiliateRef, {
          regieUid: currentRegieUid,
          regieAssignedAt: serverTimestamp(),
        });

        const regieRef = doc(firestore, 'regies', currentRegieUid);
        batch.set(
          regieRef,
          {
            regieUid: currentRegieUid,
            regieEmail: regieDoc?.email || '',
            regiePseudo: getDisplayName(regieDoc),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        const regieAffiliateRef = doc(
          firestore,
          'regies',
          currentRegieUid,
          'affiliates',
          affiliateUser.id
        );
        batch.set(
          regieAffiliateRef,
          {
            affiliateUid: affiliateUser.id,
            affiliateEmail: affiliateUser.email || '',
            affiliatePseudo: getDisplayName(affiliateUser),
            assignedAt: serverTimestamp(),
            assignedBy: auth.currentUser?.uid || null,
          },
          { merge: true }
        );

        await batch.commit();
        setFlash(`✅ ${getDisplayName(affiliateUser)} attribué à la régie.`);

        // Rafraîchir l’état local des suggestions
        setSuggestUsers((prev) =>
          prev.map((u) =>
            u.id === affiliateUser.id ? { ...u, regieUid: currentRegieUid, regieAssignedAt: new Date() } : u
          )
        );
        if (lookupAffiliate?.id === affiliateUser.id) setLookupAffiliate({ ...lookupAffiliate, regieUid: currentRegieUid });
      } catch {
        setFlash("❌ Impossible d'attribuer cet affilié.");
      } finally {
        setAssigningUid(null);
      }
    },
    [currentRegieUid, regieDoc, lookupAffiliate]
  );

  // Retrait (dans la section “déjà rattachés”)
  const unassignAffiliate = useCallback(
    async (affiliate) => {
      if (!currentRegieUid || !affiliate?.id) return;
      const ok = window.confirm(
        `Retirer ${getDisplayName(affiliate)} (${affiliate.email || '—'}) de cette régie ?`
      );
      if (!ok) return;
      try {
        setUnassigningUid(affiliate.id);
        const batch = writeBatch(firestore);
        const affiliateRef = doc(firestore, 'users', affiliate.id);
        batch.update(affiliateRef, {
          regieUid: deleteField(),
          regieAssignedAt: deleteField(),
        });
        const regieAffiliateRef = doc(
          firestore,
          'regies',
          currentRegieUid,
          'affiliates',
          affiliate.id
        );
        batch.delete(regieAffiliateRef);
        const regieRef = doc(firestore, 'regies', currentRegieUid);
        batch.set(regieRef, { updatedAt: serverTimestamp() }, { merge: true });
        await batch.commit();
        setFlash('Affilié détaché de la régie.');

        // Refléter côté suggestions
        setSuggestUsers((prev) =>
          prev.map((u) => (u.id === affiliate.id ? { ...u, regieUid: undefined, regieAssignedAt: undefined } : u))
        );
      } catch {
        setFlash("❌ Erreur lors du retrait de l'affilié.");
      } finally {
        setUnassigningUid(null);
      }
    },
    [currentRegieUid]
  );

  // --------------------------------- Sélecteurs/mémos ---------------------------------

  const filteredSuggestions = useMemo(() => {
    const s = toLc(searchInput);
    const base = suggestUsers;
    if (!s) return base;
    return base.filter((u) => {
      const label = `${getDisplayName(u)} ${u.email || ''} ${u.id}`.toLowerCase();
      return label.includes(s);
    });
  }, [suggestUsers, searchInput]);

  const totalPages = Math.max(1, Math.ceil(filteredSuggestions.length / PAGE_SIZE));
  const pageClamp = Math.min(Math.max(1, page), totalPages);
  const pageSlice = filteredSuggestions.slice((pageClamp - 1) * PAGE_SIZE, pageClamp * PAGE_SIZE);

  // ----------------------------------------- UI -----------------------------------------
  return (
    <div className="dashboard-container">
      <style>{`
        .agr-page { width: 100%; min-width: 0; display: flex; flex-direction: column; gap: 20px; }
        .agr-page .agr-cols { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 20px; align-items: start; }
        .agr-page .agr-inline { display: flex; align-items: center; gap: 12px 20px; flex-wrap: wrap; }
        .agr-page .agr-inline__label { flex: 1 1 240px; min-width: 0; }
        .agr-page .agr-inline__label .hint { margin: 2px 0 0; }
        .agr-page .agr-inline .agr-form { flex: 1 1 380px; }
        .agr-page .agr-filter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .agr-page .agr-filter .eb-search { flex: 1 1 260px; width: auto; }
        .agr-page .eb-search .eb-input { padding-left: 40px; width: 100%; }
        @media (max-width: 1100px) { .agr-page .agr-cols { grid-template-columns: minmax(0, 1fr); } }
        .agr-page .eb-page-header { margin-bottom: 0; align-items: center; }
        .dashboard-container .agr-page .eb-page-title { margin: 0; text-align: left; }
        .agr-page .card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 20px 24px; min-width: 0; }
        .agr-page .card h2 { margin: 0; font-size: 16.5px; font-weight: 650; color: var(--eb-text); text-align: left; }
        .agr-page .pill { display:inline-flex; align-items: center; padding:3px 10px; border-radius: var(--eb-radius-pill); border:1px solid var(--eb-border); background: var(--eb-surface-2); color: var(--eb-text-2); font-size:12.5px; font-weight:600; overflow-wrap: anywhere; }
        .agr-page .pill--ok { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
        .agr-page .pill--warn { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
        .agr-page .card-row { border:1px solid var(--eb-border); border-radius: var(--eb-radius); padding:14px 16px; background: var(--eb-surface); display:flex; gap:12px; align-items:center; justify-content:space-between; transition: border-color .15s ease; }
        .agr-page .card-row:hover { border-color: var(--eb-border-strong); }
        .agr-page .card-row__name { font-weight: 600; color: var(--eb-text); overflow-wrap: anywhere; }
        .agr-page .card-row__meta { font-size: 13px; color: var(--eb-muted); overflow-wrap: anywhere; }
        .agr-page .card-row__actions { display: flex; gap: 8px; flex-shrink: 0; }
        .agr-page .muted { color: var(--eb-muted); font-size: 13.5px; }
        .agr-page .list { display:grid; gap:10px }
        .agr-page .button-classic.btn-danger { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }
        .agr-page .button-classic.btn-danger:hover:not(:disabled) { background: var(--eb-danger); color: #fff; }
        .agr-page .button-classic.btn-primary { background: var(--eb-brand); color: #fff; border-color: var(--eb-brand); }
        .agr-page .button-classic.btn-primary:hover:not(:disabled) { background: var(--eb-brand-700); }
        .agr-page .button-classic.btn-secondary { }
        .agr-page .hint { font-size:13px; color: var(--eb-muted); }
        .agr-page .hint code { font-family: var(--eb-font-mono); font-size: 12.5px; background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: 4px; padding: 0 4px; }
        .agr-page .section-title { display:flex; align-items:baseline; gap:8px 12px; flex-wrap: wrap; }
        .agr-page .banner { border:1px solid #cfe0f7; background: var(--eb-info-bg); color: #1b4a80; padding:14px 16px; border-radius: var(--eb-radius); display:flex; gap:12px; align-items:flex-start; font-size: 14px; line-height: 1.6; flex-wrap: wrap; }
        .agr-page .banner strong { font-weight: 600; }
        .agr-page .input { min-height: 42px; padding: 0 12px; font-size: 14.5px; border-radius: var(--eb-radius-sm); min-width: 0; }
        .agr-page .agr-search { padding: 14px; background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); }
        .agr-page .agr-form { display:flex; gap:10px; flex-wrap:wrap; }
        .agr-page .agr-err { color: var(--eb-danger); font-weight: 500; font-size: 14px; margin: 8px 0 0; }
        .agr-page .agr-state { margin: 12px 0 0; color: var(--eb-muted); font-size: 14px; }
        @media (max-width: 600px) {
          .agr-page .card { padding: 16px; }
          .agr-page .card-row { flex-direction: column; align-items: stretch; }
          .agr-page .card-row__actions > button { flex: 1; }
          .agr-page .agr-form > * { flex: 1 1 100% !important; }
          .agr-page .agr-filter > * { flex: 1 1 100%; }
          .agr-page .agr-filter .muted { margin-left: 0 !important; }
        }
      `}</style>

      <AdminNavbar />

      <div className="agr-page">
      <header className="eb-page-header">
        <div>
          <h1 className="eb-page-title">Attribuer des affiliés à une régie</h1>
          <p className="eb-page-subtitle">Gestion des affiliés rattachés à la régie</p>
        </div>
        <button className="eb-btn eb-btn--secondary" onClick={() => navigate(-1)}>← Retour</button>
      </header>

      {/* Bandeau Régie active / fallback si manuelle */}
      <section className="card">
        {currentRegieUid ? (
          <>
            <div className="banner">
              <span className="eb-badge eb-badge--success">Régie active</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div><strong>UID :</strong> {currentRegieUid}</div>
                {regieDoc && (
                  <>
                    <div><strong>Pseudo :</strong> {getDisplayName(regieDoc)}</div>
                    <div><strong>Email :</strong> {regieDoc.email || '—'}</div>
                    <div><strong>Rôle :</strong> {regieDoc.role || '—'}</div>
                  </>
                )}
              </div>
              {regieLoading && <div className="muted">Chargement…</div>}
            </div>
            {regieWarning && !regieError && (
              <div className="eb-alert" style={{ marginTop:12, background:'var(--eb-warning-bg)', borderColor:'#fde3b0', color:'var(--eb-warning)' }}>{regieWarning}</div>
            )}
            {regieError && (
              <p className="agr-err" style={{ marginTop:12 }}>{regieError}</p>
            )}
          </>
        ) : (
          <>
            <div className="agr-inline">
            <div className="agr-inline__label">
              <h2>Sélectionner la régie</h2>
              <p className="hint">Cas rare : si tu arrives ici sans passer par la fiche utilisateur.</p>
            </div>
            <form onSubmit={handleManualRegieLoad} className="agr-form">
              <input
                className="eb-input input"
                value={regieInput}
                onChange={(e) => setRegieInput(e.target.value)}
                placeholder="Pseudo ou UID de la régie"
                style={{ flex:'1 1 260px' }}
              />
              <button className="button-classic" type="submit" disabled={regieLoading}>
                {regieLoading ? 'Chargement…' : 'Charger'}
              </button>
            </form>
            </div>
            {regieError && <p className="agr-err" style={{ marginTop:12 }}>{regieError}</p>}
          </>
        )}
      </section>

      {flash && (
        <div className="eb-alert eb-alert--success">
          {flash}
        </div>
      )}

      <div className="agr-cols">
      {/* Étape 2 — Suggestions + Recherche + Attribution */}
      <section className="card">
        <div className="section-title">
          <h2>Choisir des affiliés à attribuer</h2>
          <span className="hint">Source: <code>code promo</code> ➝ fiches <code>users</code></span>
        </div>

        {!currentRegieUid && (
          <p className="agr-state">
            Charge d’abord une régie pour afficher les suggestions.
          </p>
        )}

        {currentRegieUid && (
          <>
            {/* Recherche ciblée (pseudo/uid/email) */}
            <div className="agr-search" style={{ marginTop:16 }}>
              <form onSubmit={handleLookupAffiliate} className="agr-form">
                <input
                  className="eb-input input"
                  value={lookupInput}
                  onChange={(e) => setLookupInput(e.target.value)}
                  placeholder="Recherche ciblée : pseudo, UID ou email"
                  style={{ flex:'1 1 260px' }}
                />
                <button className="button-classic" type="submit" disabled={lookupLoading}>
                  {lookupLoading ? 'Recherche…' : 'Trouver'}
                </button>
              </form>

              {lookupError && <p className="agr-err">{lookupError}</p>}

              {lookupAffiliate && (
                <div className="card-row" style={{ marginTop:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div className="card-row__name">{getDisplayName(lookupAffiliate)}</div>
                    <div className="card-row__meta">{lookupAffiliate.email || '—'}</div>
                    <div className="card-row__meta">
                      UID: {lookupAffiliate.id} · Rôle: {lookupAffiliate.role || '—'}
                    </div>
                    <div className="card-row__meta">
                      Régie: {lookupAffiliate.regieUid || 'Aucune'} {lookupAffiliate.regieUid && '(déjà rattaché)'}
                    </div>
                    <div className="card-row__meta">
                      Attribué depuis: {formatTimestamp(lookupAffiliate.regieAssignedAt)}
                    </div>
                  </div>
                  <div className="card-row__actions">
                    <button
                      className="button-classic btn-primary"
                      onClick={() => assignAffiliate(lookupAffiliate)}
                      disabled={assigningUid === lookupAffiliate.id || !['affiliates','affiliatepending'].includes(lookupAffiliate.role)}
                    >
                      {assigningUid === lookupAffiliate.id ? 'Attribution…' : 'Attribuer'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Zone suggestions (liste complète -> filtrable) */}
            <div style={{ marginTop:16 }}>
              <div className="agr-filter">
                <div className="eb-search">
                  <FaSearch className="eb-search__icon" aria-hidden="true" />
                  <input
                    className="eb-input input"
                    value={searchInput}
                    onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                    placeholder="Filtrer par pseudo/UID/email"
                  />
                </div>
                <button className="button-classic btn-secondary" onClick={() => { setSearchInput(''); setPage(1); }}>
                  Effacer le filtre
                </button>
                <span className="muted" style={{ marginLeft:'auto', whiteSpace:'nowrap' }}>
                  {filteredSuggestions.length} résultats · page {pageClamp}/{totalPages}
                </span>
              </div>

              {suggestLoading && <p className="agr-state" style={{ display:'flex', alignItems:'center', gap:8 }}><span className="eb-spinner eb-spinner--sm" /> Chargement des suggestions…</p>}
              {suggestError && <p className="agr-err">{suggestError}</p>}

              {!suggestLoading && filteredSuggestions.length === 0 && (
                <p className="agr-state">Aucun affilié à suggérer.</p>
              )}

              {!suggestLoading && filteredSuggestions.length > 0 && (
                <>
                  <div className="list" style={{ marginTop:12 }}>
                    {pageSlice.map((u) => {
                      const alreadyHere = u.regieUid === currentRegieUid;
                      const attachedElsewhere = !!u.regieUid && u.regieUid !== currentRegieUid;
                      return (
                        <div key={u.id} className="card-row">
                          <div style={{ minWidth:0 }}>
                            <div className="card-row__name">{getDisplayName(u)}</div>
                            <div className="card-row__meta">{u.email || '—'}</div>
                            <div className="card-row__meta">
                              UID: {u.id} · Rôle: {u.role || '—'}
                            </div>
                            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                              {alreadyHere && <span className="pill pill--ok">Déjà rattaché à cette régie</span>}
                              {attachedElsewhere && <span className="pill pill--warn">Rattaché ailleurs: {u.regieUid}</span>}
                              {!u.regieUid && <span className="pill">Indépendant</span>}
                            </div>
                          </div>
                          <div className="card-row__actions">
                            <button
                              className="button-classic btn-primary"
                              onClick={() => assignAffiliate(u)}
                              disabled={assigningUid === u.id || !['affiliates','affiliatepending'].includes(u.role) || alreadyHere}
                            >
                              {assigningUid === u.id ? 'Attribution…' : (alreadyHere ? 'Attribué' : 'Attribuer')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
                    <button className="button-classic btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageClamp === 1}>Précédent</button>
                    <button className="button-classic btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageClamp === totalPages}>Suivant</button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>

      {/* Étape 3 — Affiliés déjà rattachés à la régie */}
      <section className="card">
        <div className="section-title">
          <h2>
            Affiliés rattachés {currentRegieUid ? `(${assignedAffiliates.length})` : ''}
          </h2>
        </div>

        {!currentRegieUid && <p className="agr-state">Charge d’abord une régie.</p>}

        {currentRegieUid && assignedLoading && <p className="agr-state" style={{ display:'flex', alignItems:'center', gap:8 }}><span className="eb-spinner eb-spinner--sm" /> Chargement…</p>}

        {currentRegieUid && !assignedLoading && assignedAffiliates.length === 0 && (
          <p className="agr-state">Aucun affilié rattaché pour le moment.</p>
        )}

        {currentRegieUid && !assignedLoading && assignedAffiliates.length > 0 && (
          <div className="list" style={{ marginTop:12 }}>
            {assignedAffiliates.map((a) => (
              <div key={a.id} className="card-row">
                <div style={{ minWidth:0 }}>
                  <div className="card-row__name">{getDisplayName(a)}</div>
                  <div className="card-row__meta">{a.email || '—'}</div>
                  <div className="card-row__meta">
                    UID: {a.id} · Rôle: {a.role || '—'}
                  </div>
                  <div className="card-row__meta">
                    Attribué depuis: {formatTimestamp(a.regieAssignedAt)}
                  </div>
                </div>
                <div className="card-row__actions">
                  <button
                    className="button-classic btn-danger"
                    onClick={() => unassignAffiliate(a)}
                    disabled={unassigningUid === a.id}
                  >
                    {unassigningUid === a.id ? 'Retrait…' : 'Retirer'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
      </div>
    </div>
  );
};

export default AdminGestionRegie;
